import { Link } from '@tanstack/react-router';
import { ChevronRight, ClipboardCheck } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { formatRelativeDate } from './format';
import type { DiagnosticFeedRow } from './types';

/** Résumé court du rapport (texte brut, gras retiré), ou null. */
export function diagnosticSummaryExcerpt(row: Pick<DiagnosticFeedRow, 'report'>): string | null {
  const s = row.report?.summary_md;
  if (!s) return null;
  return (
    s
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .trim() || null
  );
}

/**
 * Carte du Feed parent « Diagnostic de Léa » (migration 0325) : maîtrise du
 * programme, début du rapport, lien vers le rapport complet.
 */
export function DiagnosticFeedCard({
  data,
  viewerRole = 'parent',
}: {
  data: DiagnosticFeedRow;
  /** Parent : rapport dans l'espace parent ; tuteur/admin (Feed « tout le monde ») : espace tuteur. */
  viewerRole?: 'parent' | 'tutor';
}) {
  const name = data.student?.display_name?.trim() || 'Votre enfant';
  const first = name.split(' ')[0];
  const excerpt = diagnosticSummaryExcerpt(data);
  return (
    <Link
      to={
        viewerRole === 'tutor'
          ? '/tutor/diagnostic/$diagnosticId'
          : '/parent/diagnostic/$diagnosticId'
      }
      params={{ diagnosticId: data.id }}
      className="border-ink-100 ocean:bg-white/5 block rounded-2xl border bg-white shadow-sm active:scale-[0.99]"
    >
      <div className="space-y-2 p-4">
        <div className="flex items-center gap-2">
          <AvatarCircle name={name} src={data.student?.avatar_url ?? null} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="text-primary-600 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider">
              <ClipboardCheck className="h-3.5 w-3.5" aria-hidden /> Diagnostic terminé
            </p>
            <p className="text-ink-900 truncate text-sm font-semibold">
              {first} a fait son diagnostic
            </p>
          </div>
          {data.completed_at && (
            <span className="text-ink-400 shrink-0 text-xs">
              {formatRelativeDate(data.completed_at)}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-display text-ink-900 text-3xl font-extrabold">
            {data.mastery_pct ?? 0}%
          </span>
          <span className="text-ink-500 text-sm">
            de maîtrise du programme · {data.correct ?? 0}/{data.total ?? 0}
          </span>
        </div>
        <p className="text-ink-700 line-clamp-3 text-sm">
          {excerpt ?? 'Le rapport détaillé est prêt à être consulté.'}
        </p>
        <p className="text-primary-600 flex items-center gap-1 text-sm font-semibold">
          Voir le rapport <ChevronRight className="h-4 w-4" aria-hidden />
        </p>
      </div>
    </Link>
  );
}
